import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Slider } from '../components/ui/slider';
import { 
  ArrowLeft, ArrowRight, Stethoscope, Brain, CheckCircle,
  Award, Shield, Clock, MapPin, Video
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LICENSE_TYPES = [
  { value: 'CNA', label: 'Certified Nursing Assistant (CNA)' },
  { value: 'LPN', label: 'Licensed Practical Nurse (LPN)' },
  { value: 'RN', label: 'Registered Nurse (RN)' },
  { value: 'NP', label: 'Nurse Practitioner (NP)' },
  { value: 'MD', label: 'Medical Doctor (MD)' },
  { value: 'MSW', label: 'Medical Social Worker (MSW)' },
  { value: 'PT', label: 'Physical Therapist (PT)' },
  { value: 'OT', label: 'Occupational Therapist (OT)' }
];

const SPECIALTIES = [
  'Geriatric Care',
  'Pediatric Care',
  'Mental Health',
  'Wound Care',
  'Hospice/Palliative',
  'Post-Surgical',
  'Diabetes Management',
  'Cardiac Care',
  'Dementia/Alzheimer\'s',
  'Rehabilitation',
  'Pain Management',
  'Oncology'
];

const CERTIFICATIONS = [
  'BLS/CPR',
  'ACLS',
  'Wound Care Certified',
  'Hospice Certified',
  'Dementia Care Specialist',
  'IV Therapy',
  'Medication Administration',
  'Phlebotomy'
];

const COMFORT_DIAGNOSES = [
  { key: 'dementia', label: 'Dementia/Alzheimer\'s' },
  { key: 'hospice', label: 'Hospice/End of Life' },
  { key: 'wound_care', label: 'Complex Wound Care' },
  { key: 'behavioral', label: 'Behavioral Issues' },
  { key: 'pediatric', label: 'Pediatric Care' },
  { key: 'mental_health', label: 'Mental Health' }
];

export default function ClinicianAssessment() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  
  const [formData, setFormData] = useState({
    license_type: '',
    license_number: '',
    license_state: '',
    years_of_experience: 1,
    specialties: [],
    certifications: [],
    hourly_rate: '',
    available_for_telehealth: true,
    available_for_in_person: true,
    service_radius_miles: 25,
    bio: '',
    preferred_care_setting: 'both',
    availability_hours: 20,
    comfort_with_diagnoses: {}
  });

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  const handleNext = () => {
    if (step < totalSteps) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const toggleSpecialty = (specialty) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.includes(specialty)
        ? prev.specialties.filter(s => s !== specialty)
        : [...prev.specialties, specialty]
    }));
  };

  const toggleCertification = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert]
    }));
  };

  const updateComfortLevel = (key, value) => {
    setFormData(prev => ({
      ...prev,
      comfort_with_diagnoses: {
        ...prev.comfort_with_diagnoses,
        [key]: value[0]
      }
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // First update profile
      await axios.put(`${API}/clinician/profile`, {
        license_type: formData.license_type,
        license_number: formData.license_number,
        license_state: formData.license_state,
        years_of_experience: formData.years_of_experience,
        specialties: formData.specialties,
        certifications: formData.certifications,
        hourly_rate: parseFloat(formData.hourly_rate) || null,
        available_for_telehealth: formData.available_for_telehealth,
        available_for_in_person: formData.available_for_in_person,
        service_radius_miles: formData.service_radius_miles,
        bio: formData.bio
      }, { ...getAuthHeader(), timeout: 30000 });

      // Then run assessment
      const response = await axios.post(`${API}/clinician/assessment`, {
        years_of_experience: formData.years_of_experience,
        certifications: formData.certifications,
        specialties: formData.specialties,
        preferred_care_setting: formData.preferred_care_setting,
        comfort_with_diagnoses: formData.comfort_with_diagnoses,
        personality_responses: {},
        availability_hours: formData.availability_hours
      }, { ...getAuthHeader(), timeout: 90000 });

      setResults(response.data.results);
      toast.success('Assessment complete!');
      setStep(5);
    } catch (error) {
      const msg = error.code === 'ECONNABORTED'
        ? 'The AI is taking longer than usual. Please try again in a moment.'
        : (error.response?.data?.detail || 'Assessment failed');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-cn-sage-100 dark:from-cn-ink-900 dark:via-cn-ink-900 dark:to-cn-ink-800 py-8 px-4" data-testid="clinician-assessment">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button 
            onClick={() => step === 1 ? navigate('/clinician/dashboard') : handleBack()}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-cn-ink-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-display">
              Set up your practice
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {step <= 4 ? `Step ${step} of ${totalSteps} · about 2 minutes` : 'Your practice is ready'}
            </p>
          </div>
          <div className="w-12 h-12 bg-cn-sage-700 rounded-xl flex items-center justify-center">
            <Stethoscope className="w-6 h-6 text-white" />
          </div>
        </div>

        {/* Progress Bar */}
        {step <= 4 && (
          <div className="mb-8">
            <Progress value={progress} className="h-2" />
          </div>
        )}

        {/* Step 1: License Info */}
        {step === 1 && (
          <Card className="border-0 shadow-xl bg-white/80 dark:bg-cn-ink-800/80 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-display">
                <Shield className="w-5 h-5 text-cn-primary" />
                License Information
              </CardTitle>
              <CardDescription>Enter your professional credentials</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>License Type</Label>
                <Select 
                  value={formData.license_type}
                  onValueChange={(value) => setFormData({...formData, license_type: value})}
                >
                  <SelectTrigger data-testid="license-type-select" className="bg-slate-50 dark:bg-cn-ink-700/50">
                    <SelectValue placeholder="Select license type" />
                  </SelectTrigger>
                  <SelectContent>
                    {LICENSE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>License Number</Label>
                  <Input
                    data-testid="license-number-input"
                    placeholder="e.g., RN123456"
                    value={formData.license_number}
                    onChange={(e) => setFormData({...formData, license_number: e.target.value})}
                    className="bg-slate-50 dark:bg-cn-ink-700/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>License State</Label>
                  <Input
                    data-testid="license-state-input"
                    placeholder="e.g., TX"
                    value={formData.license_state}
                    onChange={(e) => setFormData({...formData, license_state: e.target.value})}
                    className="bg-slate-50 dark:bg-cn-ink-700/50"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Years of Experience: {formData.years_of_experience}</Label>
                <Slider
                  value={[formData.years_of_experience]}
                  onValueChange={(value) => setFormData({...formData, years_of_experience: value[0]})}
                  max={40}
                  min={0}
                  step={1}
                  className="py-4"
                />
              </div>

              <Button 
                onClick={handleNext}
                data-testid="next-btn-step1"
                disabled={!formData.license_type || !formData.license_number}
                className="w-full bg-cn-sage-700 hover:bg-cn-sage-800 text-white rounded-xl h-12"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Specialties & Certifications */}
        {step === 2 && (
          <Card className="border-0 shadow-xl bg-white/80 dark:bg-cn-ink-800/80 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-display">
                <Award className="w-5 h-5 text-cn-accent" />
                Specialties & Certifications
              </CardTitle>
              <CardDescription>Select all that apply</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Specialties</Label>
                <div className="grid grid-cols-2 gap-3">
                  {SPECIALTIES.map((specialty) => (
                    <div
                      key={specialty}
                      onClick={() => toggleSpecialty(specialty)}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.specialties.includes(specialty)
                          ? 'border-cn-primary bg-cn-sage-50 dark:bg-cn-sage-900/30'
                          : 'border-slate-200 dark:border-cn-ink-700 hover:border-slate-300 dark:hover:border-slate-600'
                      }`}
                    >
                      <Checkbox 
                        checked={formData.specialties.includes(specialty)}
                        className="pointer-events-none"
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{specialty}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Certifications</Label>
                <div className="grid grid-cols-2 gap-3">
                  {CERTIFICATIONS.map((cert) => (
                    <div
                      key={cert}
                      onClick={() => toggleCertification(cert)}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.certifications.includes(cert)
                          ? 'border-cn-primary bg-cn-sage-50 dark:bg-cn-sage-900/30'
                          : 'border-slate-200 dark:border-cn-ink-700 hover:border-slate-300 dark:hover:border-slate-600'
                      }`}
                    >
                      <Checkbox 
                        checked={formData.certifications.includes(cert)}
                        className="pointer-events-none"
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Button 
                onClick={handleNext}
                data-testid="next-btn-step2"
                disabled={formData.specialties.length === 0}
                className="w-full bg-cn-sage-700 hover:bg-cn-sage-800 text-white rounded-xl h-12"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Preferences */}
        {step === 3 && (
          <Card className="border-0 shadow-xl bg-white/80 dark:bg-cn-ink-800/80 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-display">
                <Clock className="w-5 h-5 text-orange-600" />
                Availability & Rates
              </CardTitle>
              <CardDescription>Set your preferences</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Hourly Rate ($)</Label>
                <Input
                  data-testid="hourly-rate-input"
                  type="number"
                  placeholder="e.g., 45"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                  className="bg-slate-50 dark:bg-cn-ink-700/50"
                />
              </div>

              <div className="space-y-2">
                <Label>Weekly Availability: {formData.availability_hours} hours</Label>
                <Slider
                  value={[formData.availability_hours]}
                  onValueChange={(value) => setFormData({...formData, availability_hours: value[0]})}
                  max={60}
                  min={5}
                  step={5}
                  className="py-4"
                />
              </div>

              <div className="space-y-4">
                <Label>Service Types</Label>
                <div className="flex gap-4">
                  <div
                    onClick={() => setFormData({...formData, available_for_telehealth: !formData.available_for_telehealth})}
                    className={`flex-1 flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.available_for_telehealth
                        ? 'border-cn-primary bg-cn-sage-50 dark:bg-cn-sage-900/30'
                        : 'border-slate-200 dark:border-cn-ink-700'
                    }`}
                  >
                    <Video className={`w-6 h-6 ${formData.available_for_telehealth ? 'text-cn-primary' : 'text-slate-400'}`} />
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">Telehealth</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Video visits</p>
                    </div>
                  </div>
                  <div
                    onClick={() => setFormData({...formData, available_for_in_person: !formData.available_for_in_person})}
                    className={`flex-1 flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.available_for_in_person
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/30'
                        : 'border-slate-200 dark:border-cn-ink-700'
                    }`}
                  >
                    <MapPin className={`w-6 h-6 ${formData.available_for_in_person ? 'text-green-600' : 'text-slate-400'}`} />
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">In-Person</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Home visits</p>
                    </div>
                  </div>
                </div>
              </div>

              {formData.available_for_in_person && (
                <div className="space-y-2">
                  <Label>Service Radius: {formData.service_radius_miles} miles</Label>
                  <Slider
                    value={[formData.service_radius_miles]}
                    onValueChange={(value) => setFormData({...formData, service_radius_miles: value[0]})}
                    max={100}
                    min={5}
                    step={5}
                    className="py-4"
                  />
                </div>
              )}

              <Button 
                onClick={handleNext}
                data-testid="next-btn-step3"
                disabled={!formData.hourly_rate}
                className="w-full bg-cn-sage-700 hover:bg-cn-sage-800 text-white rounded-xl h-12"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Comfort Levels */}
        {step === 4 && (
          <Card className="border-0 shadow-xl bg-white/80 dark:bg-cn-ink-800/80 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-display">
                <Brain className="w-5 h-5 text-cn-primary" />
                Care Comfort Levels
              </CardTitle>
              <CardDescription>Rate your comfort with different care scenarios (1-10)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {COMFORT_DIAGNOSES.map((diag) => (
                <div key={diag.key} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>{diag.label}</Label>
                    <span className="text-sm font-semibold text-cn-sage-700 dark:text-cn-sage-300">
                      {formData.comfort_with_diagnoses[diag.key] || 5}/10
                    </span>
                  </div>
                  <Slider
                    value={[formData.comfort_with_diagnoses[diag.key] || 5]}
                    onValueChange={(value) => updateComfortLevel(diag.key, value)}
                    max={10}
                    min={1}
                    step={1}
                    className="py-2"
                  />
                </div>
              ))}

              <div className="space-y-2">
                <Label>Professional Bio (Optional)</Label>
                <Textarea
                  data-testid="bio-input"
                  placeholder="Tell patients about your experience and approach to care..."
                  value={formData.bio}
                  onChange={(e) => setFormData({...formData, bio: e.target.value})}
                  className="min-h-[100px] bg-slate-50 dark:bg-cn-ink-700/50"
                />
              </div>

              <Button 
                onClick={handleSubmit}
                data-testid="submit-clinician-assessment-btn"
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-xl h-12 shadow-lg shadow-orange-500/20"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Analyzing with AI...</span>
                  </div>
                ) : (
                  <>
                    <Brain className="w-4 h-4 mr-2" />
                    Complete Assessment
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Results */}
        {step === 5 && results && (
          <Card className="border-0 shadow-xl bg-white/80 dark:bg-cn-ink-800/80 backdrop-blur-xl">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <CardTitle className="text-2xl font-display">Profile Complete!</CardTitle>
              <CardDescription>Your AI-powered competency analysis</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-cn-sage-50 dark:bg-cn-sage-900/20 rounded-xl p-4 text-center">
                  <p className="text-sm text-cn-primary dark:text-cn-sage-300 font-medium">Competency Score</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{results.competency_score}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">out of 100</p>
                </div>
                <div className="bg-cn-terracotta-50 dark:bg-cn-terracotta-700/20 rounded-xl p-4 text-center">
                  <p className="text-sm text-cn-accent dark:text-cn-terracotta-300 font-medium">Care Capacity</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{results.care_intensity_capacity}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">out of 100</p>
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-cn-ink-700/50 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 dark:text-slate-400">Risk Tolerance</span>
                  <span className="font-semibold text-slate-900 dark:text-white">{results.risk_tolerance_category}</span>
                </div>
              </div>

              {results.strengths?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">Your Strengths</p>
                  <div className="flex flex-wrap gap-2">
                    {results.strengths.map((strength, i) => (
                      <span key={i} className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm">
                        {strength}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {results.recommended_case_types?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">Recommended Case Types</p>
                  <div className="flex flex-wrap gap-2">
                    {results.recommended_case_types.map((type, i) => (
                      <span key={i} className="px-3 py-1 bg-cn-sage-100 dark:bg-cn-sage-900/30 text-cn-primary-hover dark:text-cn-primary rounded-full text-sm">
                        {type}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <Button 
                onClick={() => navigate('/clinician/dashboard')}
                data-testid="go-to-clinician-dashboard-btn"
                className="w-full bg-cn-sage-700 hover:bg-cn-sage-800 text-white rounded-xl h-12"
              >
                View Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
